import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Navigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../../css/phone-input.css';
import '../../css/profile.css';

const Profile = () => {
  const { user, isAuthenticated, logout, updateUser } = useAuth();
  const { showToast } = useToast();
  const [profileData, setProfileData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    email_verified: false,
    phone_number: '',
    company_name: '',
    industry: '',
    job_title: '',
    newsletter_subscription: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [showAddEmailForm, setShowAddEmailForm] = useState(false);

  useEffect(() => {
    const fetchFreshProfile = async () => {
      try {
        // Fetch fresh profile data from API
        const response = await authAPI.getProfile();
        const freshUser = response.data;

        // Update local state with fresh data
        setProfileData({
          username: freshUser.username || '',
          first_name: freshUser.first_name || '',
          last_name: freshUser.last_name || '',
          email: freshUser.email || '',
          email_verified: freshUser.email_verified || false,
          phone_number: freshUser.phone_number || '',
          company_name: freshUser.company_name || '',
          industry: freshUser.industry || '',
          job_title: freshUser.job_title || '',
          newsletter_subscription: freshUser.newsletter_subscription || false
        });

        // Update global user context with fresh data
        updateUser(freshUser);
      } catch (error) {
        console.error('Error fetching fresh profile:', error);
        // Fall back to cached user data
        if (user) {
          setProfileData({
            username: user.username || '',
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            email_verified: user.email_verified || false,
            phone_number: user.phone_number || '',
            company_name: user.company_name || '',
            industry: user.industry || '',
            job_title: user.job_title || '',
            newsletter_subscription: user.newsletter_subscription || false
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchFreshProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only fetch profile once on mount

  // Check for email verification success in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verified = params.get('verified');

    if (verified === 'true') {
      setShowVerificationBanner(true);
      setEmailStatus('success');

      // Auto-hide banner after 30 seconds
      setTimeout(() => {
        setShowVerificationBanner(false);
        setEmailStatus('');
      }, 30000);

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleProfileInputChange = (field, value) => {
    setProfileData({ ...profileData, [field]: value });
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const response = await authAPI.uploadAvatar(file);
      updateUser(response.data);
      showToast('Profile photo updated!', 'success');
    } catch {
      showToast('Failed to upload photo. Please try a JPEG or PNG.', 'error');
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await authAPI.updateProfile(profileData);
      updateUser(response.data);
      showToast('Profile updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);

      // Handle validation errors for username field
      if (error.response?.data?.username) {
        const usernameErrors = Array.isArray(error.response.data.username)
          ? error.response.data.username.join(' ')
          : error.response.data.username;
        showToast(usernameErrors, 'error');
      } else {
        showToast(error.response?.data?.detail || 'Failed to update profile', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  // Fetch additional emails
  const fetchAdditionalEmails = async () => {
    try {
      const response = await authAPI.get('/accounts/emails/');
      // Backend returns paginated data: {count, next, previous, results}
      // We need the results array
      const emails = response.data.results || response.data;
      setAdditionalEmails(emails);
    } catch (error) {
      console.error('Failed to fetch additional emails:', error);
    }
  };

  // Add new email
  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      setEmailStatus('Please enter an email address');
      setTimeout(() => setEmailStatus(''), 3000);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailStatus('Please enter a valid email address');
      setTimeout(() => setEmailStatus(''), 3000);
      return;
    }

    const emailToAdd = newEmail;
    try {
      await authAPI.post('/accounts/emails/', { email: emailToAdd });
      setEmailStatus(`${emailToAdd} added! Check your inbox for verification link.`);
      setNewEmail('');
      setShowAddEmailForm(false);
      await fetchAdditionalEmails();
      setTimeout(() => setEmailStatus(''), 5000);
    } catch (error) {
      console.error('Failed to add email:', error);
      const errorMsg = error.response?.data?.email?.[0] || 'Failed to add email';
      setEmailStatus(errorMsg);
      setTimeout(() => setEmailStatus(''), 5000);
    }
  };

  // Delete email
  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('Are you sure you want to remove this email?')) {
      return;
    }

    try {
      await authAPI.delete(`/accounts/emails/${emailId}/`);
      await fetchAdditionalEmails();
    } catch (error) {
      console.error('Failed to delete email:', error);
      setEmailStatus('Failed to remove email');
      setTimeout(() => setEmailStatus(''), 3000);
    }
  };

  // Fetch emails on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchAdditionalEmails();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/signin" />;
  }

  if (loading) {
    return (
      <div className="profile-page" style={{ padding: '40px 20px', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#ccc' }}>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page" style={{ padding: '40px 20px', minHeight: '60vh' }}>
      <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '30px', color: '#fff' }}>My Account</h1>

        {/* Email Verification Banner */}
        {showVerificationBanner && emailStatus === 'success' && (
          <div style={{
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '500',
            border: '2px solid #4caf50'
          }}>
            ✓ Email verified successfully!
          </div>
        )}

        {/* Profile Form */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <form onSubmit={handleSaveProfile}>
            {/* Avatar upload */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <label style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', background: 'linear-gradient(135deg, var(--pink), var(--violet))', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(255,255,255,0.15)' }}>
                  {avatarUploading ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg>
                  ) : user?.profile_image ? (
                    <img src={user.profile_image} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: '2rem', fontWeight: '700', color: '#fff' }}>
                      {(user?.first_name || user?.username || '?')[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: '24px', height: '24px', borderRadius: '50%', background: 'var(--pink)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #1a1a2e' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                </div>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatarChange} style={{ display: 'none' }} />
              </label>
              <div>
                <div style={{ color: '#fff', fontWeight: '600', marginBottom: '4px' }}>Profile Photo</div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>Click the photo to upload. JPEG, PNG, or WebP · Cropped to 128×128.</div>
              </div>
            </div>

            <div className="profile-grid-2" style={{ marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={profileData.first_name}
                  onChange={(e) => handleProfileInputChange('first_name', e.target.value)}
                  placeholder="First name"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={profileData.last_name}
                  onChange={(e) => handleProfileInputChange('last_name', e.target.value)}
                  placeholder="Last name"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                Email
              </label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => handleProfileInputChange('email', e.target.value)}
                placeholder="Email address"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                Username
              </label>
              <input
                type="text"
                value={profileData.username}
                onChange={(e) => handleProfileInputChange('username', e.target.value)}
                placeholder="Username"
                disabled={user && !user.can_change_username}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: user && !user.can_change_username ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  boxSizing: 'border-box',
                  cursor: user && !user.can_change_username ? 'not-allowed' : 'text'
                }}
              />
              {user && !user.can_change_username && (
                <p style={{
                  fontSize: '12px',
                  color: '#ff9800',
                  marginTop: '6px',
                  marginBottom: '0'
                }}>
                  You can change your username again in {user.days_until_username_change} day{user.days_until_username_change !== 1 ? 's' : ''}
                </p>
              )}
              {user && user.can_change_username && (
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginTop: '6px',
                  marginBottom: '0'
                }}>
                  Username can only be changed once every 30 days. Choose carefully!
                </p>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                Phone Number
              </label>
              <PhoneInput
                international
                defaultCountry="US"
                countryCallingCodeEditable={false}
                value={profileData.phone_number}
                onChange={(value) => handleProfileInputChange('phone_number', value || '')}
                placeholder="Not provided"
                style={{ width: '100%' }}
                className="custom-phone-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                Company Name
              </label>
              <input
                type="text"
                value={profileData.company_name}
                onChange={(e) => handleProfileInputChange('company_name', e.target.value)}
                placeholder="Company name"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div className="profile-grid-2" style={{ marginBottom: '30px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                  Industry
                </label>
                <input
                  type="text"
                  value={profileData.industry}
                  onChange={(e) => handleProfileInputChange('industry', e.target.value)}
                  placeholder="Industry"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500' }}>
                  Job Title
                </label>
                <input
                  type="text"
                  value={profileData.job_title}
                  onChange={(e) => handleProfileInputChange('job_title', e.target.value)}
                  placeholder="Job title"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Newsletter Subscription Toggle */}
            <div style={{
              marginTop: '30px',
              marginBottom: '30px',
              padding: '20px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: '500',
                    marginBottom: '5px'
                  }}>
                    Subscribe to Newsletter
                  </label>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '14px',
                    margin: 0
                  }}>
                    Receive updates, insights, and news about AI and consulting
                  </p>
                </div>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '60px',
                  height: '34px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={profileData.newsletter_subscription}
                    onChange={(e) => handleProfileInputChange('newsletter_subscription', e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: profileData.newsletter_subscription ? 'var(--pink)' : 'rgba(255, 255, 255, 0.2)',
                    transition: '0.4s',
                    borderRadius: '34px',
                    boxShadow: profileData.newsletter_subscription ? '0 0 10px rgba(216, 134, 255, 0.5)' : 'none'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '26px',
                      width: '26px',
                      left: profileData.newsletter_subscription ? '30px' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%'
                    }}></span>
                  </span>
                </label>
              </div>
            </div>

            {/* Email Management Section */}
            <div style={{
              marginTop: '40px',
              paddingTop: '30px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h2 style={{ color: '#fff', marginBottom: '20px', fontSize: '20px' }}>Email Management</h2>

              {/* Primary Email */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '10px' }}>Primary Email</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#fff' }}>{profileData.email}</span>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    background: profileData.email_verified ? 'var(--pink)' : 'rgba(255, 169, 77, 0.2)',
                    color: profileData.email_verified ? '#fff' : '#ffa94d'
                  }}>
                    {profileData.email_verified ? 'Verified ✓' : 'Unverified'}
                  </span>
                </div>
              </div>

              {/* Additional Emails */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ color: '#fff', fontSize: '16px', margin: 0 }}>Additional Emails</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddEmailForm(!showAddEmailForm)}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      border: '1px solid var(--pink)',
                      outline: 'none',
                      borderRadius: '6px',
                      background: 'transparent',
                      color: 'var(--pink)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {showAddEmailForm ? 'Cancel' : '+ Add Email'}
                  </button>
                </div>

                {/* Add Email Form */}
                {showAddEmailForm && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter email address"
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        marginBottom: '10px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddEmail}
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        border: 'none',
                        outline: 'none',
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, var(--pink) 0%, var(--violet) 100%)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Add Email
                    </button>
                  </div>
                )}

                {/* Email Status Messages */}
                {emailStatus && (
                  <p style={{
                    color: emailStatus.includes('Failed') || emailStatus.includes('Please') ? '#ff6b6b' : 'var(--pink)',
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}>
                    {emailStatus}
                  </p>
                )}

                {/* List of Additional Emails */}
                {additionalEmails.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {additionalEmails.map((email) => (
                      <div
                        key={email.id}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          padding: '12px 15px',
                          borderRadius: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <p style={{ margin: 0, color: '#fff', fontSize: '14px' }}>{email.email}</p>
                          <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: email.is_verified ? 'var(--pink)' : '#ffa94d' }}>
                            {email.is_verified ? 'Verified ✓' : 'Pending Verification'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteEmail(email.id)}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            border: '1px solid #ff6b6b',
                            outline: 'none',
                            borderRadius: '4px',
                            background: 'transparent',
                            color: '#ff6b6b',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
                    No additional emails added yet.
                  </p>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '12px 28px',
                  fontSize: '16px',
                  border: 'none',
                  outline: 'none',
                  borderRadius: '8px',
                  background: saving ? 'rgba(255, 255, 255, 0.3)' : 'linear-gradient(135deg, var(--pink) 0%, var(--violet) 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 15px rgba(216, 134, 255, 0.4)',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(216, 134, 255, 0.6)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(216, 134, 255, 0.4)';
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>

              <button
                type="button"
                onClick={logout}
                style={{
                  padding: '12px 28px',
                  fontSize: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  outline: 'none',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
              >
                Sign Out
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
